import { useEffect, useState } from 'react';
import { expenseAPI, incomeAPI, categoryAPI, assetAPI, aiAPI, creditCardAPI, loanAPI } from '../api';
import { Plus, Search, Filter, Trash2, Edit, Calendar, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';

export default function Transactions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('expenses');
  const [expenses, setExpenses] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [nlInput, setNlInput] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
    const action = searchParams.get('action');
    if (action === 'add-expense') {
      setTab('expenses');
      setShowAddModal(true);
      searchParams.delete('action');
      setSearchParams(searchParams);
    } else if (action === 'add-income') {
      setTab('incomes');
      setShowAddModal(true);
      searchParams.delete('action');
      setSearchParams(searchParams);
    }
  }, []);

  const loadData = async () => {
    try {
      const [expensesRes, incomesRes, categoriesRes, assetsRes] = await Promise.all([
        expenseAPI.getAll(),
        incomeAPI.getAll(),
        categoryAPI.getAll(),
        assetAPI.getAll(),
      ]);
      setExpenses(expensesRes.data);
      setIncomes(incomesRes.data);
      setCategories(categoriesRes.data);
      setAssets(assetsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load transactions');
      setLoading(false);
    }
  };

  const handleNLParse = async () => {
    if (!nlInput.trim()) return;
    
    try {
      const res = await aiAPI.parseTransaction(nlInput);
      if (res.data.error) {
        toast.error('Could not parse transaction');
        return;
      }
      
      const parsedData = res.data;
      const today = new Date().toISOString().split('T')[0];
      
      if (parsedData.type === 'expense') {
        await expenseAPI.create({
          ...parsedData,
          date: today,
          recurring: false,
          tags: [],
        });
        toast.success('Expense added successfully!');
        setTab('expenses');
      } else if (parsedData.type === 'income') {
        await incomeAPI.create({
          date: today,
          amount: parsedData.amount,
          currency: parsedData.currency || 'SAR',
          source: 'other',
          description: parsedData.description,
          recurring: false,
          tags: [],
        });
        toast.success('Income added successfully!');
        setTab('incomes');
      }
      
      setNlInput('');
      loadData();
    } catch (error) {
      console.error('Error parsing transaction:', error);
      toast.error('Failed to parse transaction');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await expenseAPI.delete(id);
      toast.success('Expense deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const handleDeleteIncome = async (id) => {
    if (!window.confirm('Delete this income?')) return;
    try {
      await incomeAPI.delete(id);
      toast.success('Income deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting income:', error);
      toast.error('Failed to delete income');
    }
  };

  const filteredExpenses = expenses
    .filter((e) => !filterCategory || e.category === filterCategory)
    .filter((e) => !searchTerm || e.description.toLowerCase().includes(searchTerm.toLowerCase()));

  const filteredIncomes = incomes
    .filter((i) => !searchTerm || i.description.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-900 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 p-6 md:p-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-green-900 mb-2">
            Transactions
          </h1>
          <p className="text-stone-500 text-lg">Track your income and expenses</p>
        </div>

        {/* NLP Input */}
        <div className="mb-8 bg-gradient-to-r from-green-50 to-lime-50 rounded-2xl p-6 border border-lime-200" data-testid="nlp-input-section">
          <div className="flex items-center mb-3">
            <Sparkles className="w-5 h-5 text-green-900 mr-2" />
            <h3 className="font-bold text-green-900">Quick Add with AI</h3>
          </div>
          <p className="text-sm text-stone-600 mb-4">
            Type naturally: "Coffee 18 SAR from card" or "Received salary 20,000 SAR"
          </p>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Enter transaction in plain language..."
              value={nlInput}
              onChange={(e) => setNlInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleNLParse()}
              data-testid="nlp-input"
              className="flex-1 bg-white border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            />
            <button
              onClick={handleNLParse}
              data-testid="nlp-parse-btn"
              className="bg-green-900 text-white px-6 py-3 rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95"
            >
              Add
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setTab('expenses')}
            data-testid="tab-expenses"
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              tab === 'expenses'
                ? 'bg-green-900 text-white shadow-lg'
                : 'bg-white text-stone-600 hover:bg-stone-100'
            }`}
          >
            Expenses ({expenses.length})
          </button>
          <button
            onClick={() => setTab('incomes')}
            data-testid="tab-incomes"
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              tab === 'incomes'
                ? 'bg-green-900 text-white shadow-lg'
                : 'bg-white text-stone-600 hover:bg-stone-100'
            }`}
          >
            Incomes ({incomes.length})
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
              className="w-full pl-12 pr-4 py-3 bg-white border border-stone-200 rounded-xl focus:border-green-900 focus:ring-2 focus:ring-green-900/20 transition-all"
            />
          </div>
          {tab === 'expenses' && (
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                data-testid="filter-category"
                className="pl-12 pr-8 py-3 bg-white border border-stone-200 rounded-xl focus:border-green-900 focus:ring-2 focus:ring-green-900/20 transition-all appearance-none"
              >
                <option value="">All Categories</option>
                {categories
                  .filter((c) => c.type === 'expense')
                  .map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
              </select>
            </div>
          )}
          <button
            onClick={() => setShowAddModal(true)}
            data-testid="add-transaction-btn"
            className="bg-green-900 text-white px-6 py-3 rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95 whitespace-nowrap"
          >
            <Plus className="inline-block w-5 h-5 mr-2" />
            Add {tab === 'expenses' ? 'Expense' : 'Income'}
          </button>
        </div>

        {/* List */}
        {tab === 'expenses' && (
          <div className="space-y-3" data-testid="expenses-list">
            {filteredExpenses.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-stone-200">
                <p className="text-stone-500">No expenses found</p>
              </div>
            ) : (
              filteredExpenses.map((expense) => (
                <div
                  key={expense.id}
                  className="bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-md transition-all"
                  data-testid={`expense-${expense.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 bg-stone-100 text-stone-700 text-xs font-bold rounded-full">
                          {expense.category}
                        </span>
                        <span className="text-xs text-stone-500">
                          {format(new Date(expense.date), 'MMM d, yyyy')}
                        </span>
                      </div>
                      <p className="text-stone-900 font-medium mb-1">{expense.description}</p>
                      <p className="text-xs text-stone-500">
                        {expense.payment_method} {expense.account && `- ${expense.account}`}
                      </p>
                    </div>
                    <div className="text-right flex items-center gap-3">
                      <div>
                        <p className="text-2xl font-bold text-red-700 tabular-nums">
                          {expense.amount.toFixed(2)}
                        </p>
                        <p className="text-xs text-stone-500">{expense.currency}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteExpense(expense.id)}
                        data-testid={`delete-expense-${expense.id}`}
                        className="p-2 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'incomes' && (
          <div className="space-y-3" data-testid="incomes-list">
            {filteredIncomes.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-stone-200">
                <p className="text-stone-500">No incomes found</p>
              </div>
            ) : (
              filteredIncomes.map((income) => (
                <div
                  key={income.id}
                  className="bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-md transition-all"
                  data-testid={`income-${income.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 bg-green-100 text-green-900 text-xs font-bold rounded-full">
                          {income.source}
                        </span>
                        <span className="text-xs text-stone-500">
                          {format(new Date(income.date), 'MMM d, yyyy')}
                        </span>
                      </div>
                      <p className="text-stone-900 font-medium mb-1">{income.description}</p>
                      {income.account && (
                        <p className="text-xs text-stone-500">{income.account}</p>
                      )}
                    </div>
                    <div className="text-right flex items-center gap-3">
                      <div>
                        <p className="text-2xl font-bold text-green-900 tabular-nums">
                          +{income.amount.toFixed(2)}
                        </p>
                        <p className="text-xs text-stone-500">{income.currency}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteIncome(income.id)}
                        data-testid={`delete-income-${income.id}`}
                        className="p-2 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <AddTransactionModal
          type={tab === 'expenses' ? 'expense' : 'income'}
          categories={categories}
          assets={assets}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function AddTransactionModal({ type, categories, assets, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    currency: 'SAR',
    description: '',
    category: '',
    payment_method: 'cash',
    account: '',
    source: 'other',
    credit_card_id: '',
    loan_id: '',
  });
  const [loadingCategory, setLoadingCategory] = useState(false);
  const [creditCards, setCreditCards] = useState([]);
  const [loans, setLoans] = useState([]);
  
  const currencies = ['SAR', 'USD', 'INR', 'EUR', 'GBP', 'AED', 'JPY', 'CNY'];
  
  useEffect(() => {
    loadAdditionalData();
  }, []);
  
  const loadAdditionalData = async () => {
    try {
      const [cardsRes, loansRes] = await Promise.all([
        creditCardAPI.getAll(),
        loanAPI.getAll(),
      ]);
      setCreditCards(cardsRes.data);
      setLoans(loansRes.data);
    } catch (error) {
      console.error('Error loading additional data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (type === 'expense') {
        await expenseAPI.create({
          ...formData,
          amount: parseFloat(formData.amount),
          recurring: false,
          tags: [],
          credit_card_id: formData.credit_card_id || null,
          loan_id: formData.loan_id || null,
        });
        toast.success('Expense added successfully!');
      } else {
        await incomeAPI.create({
          date: formData.date,
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          source: formData.source,
          account: formData.account,
          description: formData.description,
          recurring: false,
          tags: [],
        });
        toast.success('Income added successfully!');
      }
      onSuccess();
    } catch (error) {
      console.error('Error adding transaction:', error);
      toast.error('Failed to add transaction');
    }
  };

  const handleAISuggest = async () => {
    if (!formData.description) return;
    setLoadingCategory(true);
    try {
      const res = await aiAPI.categorize(formData.description);
      setFormData({ ...formData, category: res.data.suggested_category });
      toast.success(`Suggested: ${res.data.suggested_category}`);
    } catch (error) {
      console.error('Error getting AI suggestion:', error);
    }
    setLoadingCategory(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-transaction-modal">
        <h2 className="text-2xl font-bold font-heading text-green-900 mb-6">
          Add {type === 'expense' ? 'Expense' : 'Income'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              data-testid="input-date"
              className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Amount</label>
            <div className="flex gap-2">
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                data-testid="input-amount"
                className="flex-1 bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
              />
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                data-testid="input-currency"
                className="w-24 bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-3 transition-all"
              >
                {currencies.map((curr) => (
                  <option key={curr} value={curr}>
                    {curr}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              data-testid="input-description"
              className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            />
          </div>
          {type === 'expense' && (
            <>
              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">Category</label>
                <div className="flex gap-2">
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    data-testid="input-category"
                    className="flex-1 bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
                  >
                    <option value="">Select category</option>
                    {categories
                      .filter((c) => c.type === 'expense')
                      .map((cat) => (
                        <option key={cat.id} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAISuggest}
                    disabled={loadingCategory}
                    data-testid="ai-suggest-btn"
                    className="px-4 bg-lime-300 text-green-900 rounded-xl hover:bg-lime-400 transition-all disabled:opacity-50"
                  >
                    <Sparkles className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">Payment Method</label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value, account: '', credit_card_id: '', loan_id: '' })}
                  required
                  data-testid="input-payment-method"
                  className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
                >
                  <option value="cash">Cash</option>
                  <option value="bank">Bank</option>
                  <option value="card">Card</option>
                  <option value="wallet">Wallet</option>
                  <option value="loan">Loan</option>
                </select>
              </div>
              
              {/* Conditional dropdowns based on payment method */}
              {formData.payment_method === 'card' && (
                <div>
                  <label className="block text-sm font-bold text-stone-700 mb-2">Select Credit Card</label>
                  <select
                    value={formData.credit_card_id}
                    onChange={(e) => setFormData({ ...formData, credit_card_id: e.target.value })}
                    required
                    data-testid="input-credit-card"
                    className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
                  >
                    <option value="">Select card</option>
                    {creditCards.map((card) => (
                      <option key={card.id} value={card.id}>
                        {card.card_name} ({card.bank_name}) **** {card.last4}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {formData.payment_method === 'bank' && (
                <div>
                  <label className="block text-sm font-bold text-stone-700 mb-2">Select Bank Account</label>
                  <select
                    value={formData.account}
                    onChange={(e) => setFormData({ ...formData, account: e.target.value })}
                    required
                    data-testid="input-bank-account"
                    className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
                  >
                    <option value="">Select bank account</option>
                    {assets
                      .filter((asset) => asset.type === 'bank')
                      .map((asset) => (
                        <option key={asset.id} value={asset.id}>
                          {asset.name} - {asset.current_balance.toFixed(2)} {asset.currency}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              
              {formData.payment_method === 'loan' && (
                <div>
                  <label className="block text-sm font-bold text-stone-700 mb-2">Select Loan</label>
                  <select
                    value={formData.loan_id}
                    onChange={(e) => setFormData({ ...formData, loan_id: e.target.value })}
                    required
                    data-testid="input-loan"
                    className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
                  >
                    <option value="">Select loan</option>
                    {loans
                      .filter((loan) => loan.role === 'I_owe' && loan.status === 'active')
                      .map((loan) => (
                        <option key={loan.id} value={loan.id}>
                          {loan.lender_or_borrower_name} - Outstanding: {loan.outstanding_amount.toFixed(2)} {loan.currency}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </>
          )}
          {type === 'income' && (
            <div>
              <label className="block text-sm font-bold text-stone-700 mb-2">Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                required
                data-testid="input-source"
                className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
              >
                <option value="salary">Salary</option>
                <option value="business">Business</option>
                <option value="rental">Rental</option>
                <option value="dividends">Dividends</option>
                <option value="other">Other</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Account (Optional)</label>
            <select
              value={formData.account}
              onChange={(e) => setFormData({ ...formData, account: e.target.value })}
              data-testid="input-account"
              className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            >
              <option value="">None</option>
              {assets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.name} ({asset.type})
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              data-testid="modal-cancel-btn"
              className="flex-1 px-6 py-3 bg-stone-100 text-stone-900 rounded-full font-medium hover:bg-stone-200 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="modal-submit-btn"
              className="flex-1 px-6 py-3 bg-green-900 text-white rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95"
            >
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
